/**
 * SelectField — a single-choice `select` attribute (PIM Wave P2, Task 3).
 * Options arrive pre-ordered + non-archived from the form-schema; their labels
 * are trilingual (RU-fallback). The empty option is the "not answered" state.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import {
  pickOptionalText,
  pickText,
} from "@/features/seller-dashboard/attributes/formSchema";
import {
  ATTR_INPUT_CLASS,
  ATTR_INPUT_ERROR_CLASS,
  FieldShell,
  type AttributeFieldProps,
} from "@/features/seller-dashboard/attributes/fields/FieldShell";

export function SelectField({
  field,
  language,
  value,
  onChange,
  onBlur,
  error,
  disabled,
}: AttributeFieldProps): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const label = pickText(field.label, language);
  const hint = pickOptionalText(field.hint, language);
  const controlId = `attr-${field.field_slug}`;
  const options = field.options ?? [];
  return (
    <FieldShell
      slug={field.field_slug}
      label={label}
      requirement={field.requirement}
      hint={hint}
      error={error}
    >
      <select
        id={controlId}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={[ATTR_INPUT_CLASS, error ? ATTR_INPUT_ERROR_CLASS : ""].join(" ")}
      >
        <option value="">{t("attributes.selectNone")}</option>
        {options.map((option) => (
          <option key={option.value_slug} value={option.value_slug}>
            {pickText(option.label, language)}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export default SelectField;
